import React from 'react'
import styled from 'styled-components'

export interface StatusBadgeProps {
    label: string
    /** Fill color (status hex). The border is derived as a slightly darker shade of it. */
    color: string
    multiline?: boolean
    className?: string
}

/** Darken a hex color by multiplying each channel by (1 - amount). */
const darken = (hex: string, amount = 0.15): string => {
    const h = hex.replace('#', '')
    const full =
        h.length === 3
            ? h
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : h
    const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
    const r = clamp(parseInt(full.slice(0, 2), 16) * (1 - amount))
    const g = clamp(parseInt(full.slice(2, 4), 16) * (1 - amount))
    const b = clamp(parseInt(full.slice(4, 6), 16) * (1 - amount))
    return `rgb(${r}, ${g}, ${b})`
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
    label,
    color,
    multiline = false,
    className,
}) => {
    const content = multiline ? label.replace(/\s+/g, '\n') : label
    return (
        <Root
            className={className}
            $bg={color}
            $border={darken(color)}
            $multiline={multiline}
        >
            {content}
        </Root>
    )
}

export default StatusBadge

const Root = styled.span<{ $bg: string; $border: string; $multiline: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    padding: 0.25rem 0.625rem;
    border-radius: 0.75rem;
    background: ${({ $bg }) => $bg};
    border: 1px solid ${({ $border }) => $border};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    white-space: ${({ $multiline }) => ($multiline ? 'pre-line' : 'nowrap')};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`
